import { useState, useEffect, useCallback } from "react";
import { createBulkPunte, getRoundStatus, getPunteByBeoordelaar, getNotasBySpanAndBeoordelaar, createNota, updateNota } from "../services/merk_services";
import { notifyBulkPunte } from "../services/notify_service"
import { fetchAllTeams } from "../services/span_services";
import { getMerkbladsByRondteId } from "../services/merkblad_services";
import { getSkedule, getSkeduleVirRondte, getRondtesLys } from "../services/rondte_services";
import { usePulsy } from 'pulsy';

// Kriteria details (description, default total and gids) come with the merkblad rows,
// so no separate request per kriteria is needed
const kriteriaDetailsFrom = (merkbladsData) => {
  const details = {};
  merkbladsData.forEach(merkblad => {
    details[merkblad.kriteria_id] = {
      beskrywing: merkblad.kriteria_beskrywing,
      default_totaal: merkblad.default_totaal,
      gids: merkblad.gids
    };
  });
  return details;
};

// How often to check for a closed round or the next round starting
const SKEDULE_INTERVAL_MS = 30000;

// The time a beoordelaar has been at the current team. The start moment is kept in this
// browser, so the timer keeps running even if the app is closed and opened again.
const SPAN_TYD_SLEUTEL = 'melktert_span_tyd';

const leesSpanTyd = () => {
  try {
    const gestoor = JSON.parse(localStorage.getItem(SPAN_TYD_SLEUTEL) || 'null');
    return gestoor && typeof gestoor.begin === 'number' ? gestoor : null;
  } catch {
    return null;
  }
};

const stoorSpanTyd = (spanId, rondteId) => {
  const inskrywing = { span_id: spanId, rondte_id: rondteId, begin: Date.now() };
  try {
    localStorage.setItem(SPAN_TYD_SLEUTEL, JSON.stringify(inskrywing));
  } catch {
    // not available (e.g. private window): the timer still works for this visit
  }
  return inskrywing.begin;
};

// mm:ss, or h:mm:ss once past an hour
const formatTyd = (sekondes) => {
  const uur = Math.floor(sekondes / 3600);
  const minute = Math.floor((sekondes % 3600) / 60);
  const sek = sekondes % 60;
  return uur > 0
    ? `${uur}:${String(minute).padStart(2, '0')}:${String(sek).padStart(2, '0')}`
    : `${minute}:${String(sek).padStart(2, '0')}`;
};

function Merk() {
  const [message, setMessage] = useState("");
  const [rondteId, setRondteId] = useState(1); // default rondte ID is 1 vir HA1
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [merkblads, setMerkblads] = useState([]);
  const [kriteriaDetails, setKriteriaDetails] = useState({});
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [roundStatus, setRoundStatus] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [auth] = usePulsy('auth');
  const [showGids, setShowGids] = useState(false); // Toggle for gids per kriteria
  const [notaValue, setNotaValue] = useState("");
  const [notaId, setNotaId] = useState(null);
  const [myPunte, setMyPunte] = useState([]); // every punt this beoordelaar has given
  // The current round with this beoordelaar's team per session - undefined while loading:
  // { rondte: { rondte_id, naam, is_oop } | null, vrye_keuse, sessies: [{ nommer, begin_tyd, eind_tyd, span, opsioneel }] }
  const [skedule, setSkedule] = useState(undefined);
  const [rondtesLys, setRondtesLys] = useState([]); // started rounds, for the round selector
  const [gekoseRondteId, setGekoseRondteId] = useState(null); // null = the current round
  const [spanBegin, setSpanBegin] = useState(null); // when the timer for this team started
  const [verstreke, setVerstreke] = useState(0); // seconds at the current team

  const username = auth?.user?.username;
  const roundClosed = !!(roundStatus && !roundStatus.is_oop);
  const huidigeRondteId = rondtesLys.find(r => r.is_huidige)?.rondte_id ?? null;
  const isVorigeRondte = gekoseRondteId !== null && gekoseRondteId !== huidigeRondteId;
  // With a schedule the beoordelaar only sees their allocated teams; without allocations
  // in the round (vrye_keuse) they may pick any team
  const metSkedule = !!(skedule && skedule.rondte && !skedule.vrye_keuse);

  const loadSkedule = useCallback(async (rondteId = null) => {
    try {
      const data = rondteId ? await getSkeduleVirRondte(rondteId) : await getSkedule();
      // Keep the same object when nothing changed, so the page doesn't reload every poll
      setSkedule(prev => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
      if (data.rondte) setRondteId(data.rondte.rondte_id);
    } catch (err) {
      // Without a schedule, fall back to marking any team in the first round
      setSkedule(prev => prev || { rondte: { rondte_id: 1, naam: 'Rondte 1', is_oop: true }, vrye_keuse: true, sessies: [] });
    }
  }, []);

  // Load the schedule, and check it regularly: the admin may close the round or start the next one.
  // While the beoordelaar is looking at an earlier round, keep showing that round.
  useEffect(() => {
    loadSkedule(gekoseRondteId);
    const interval = setInterval(() => loadSkedule(gekoseRondteId), SKEDULE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadSkedule, username, gekoseRondteId]);

  // The started rounds, for the round selector (so a beoordelaar can look back)
  useEffect(() => {
    getRondtesLys()
      .then(lys => setRondtesLys(lys.filter(r => r.gestart)))
      .catch(() => {});
  }, []);

  // A new round: start without a team selected
  useEffect(() => {
    setSelectedTeam(null);
    setMessage("");
  }, [rondteId]);

  // Tick the team timer once a second while a team is selected
  useEffect(() => {
    if (!selectedTeam || !spanBegin) return;
    const tick = () => setVerstreke(Math.max(0, Math.floor((Date.now() - spanBegin) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [selectedTeam, spanBegin]);

  // Load the teams, the merkblad and this beoordelaar's punte together,
  // so the team list can show which teams are already marked
  useEffect(() => {
    if (!skedule || !skedule.rondte) return; // wait until we know the current round
    const loadTeams = async () => {
      try {
        setLoading(true);
        const [teamsData, merkbladsData, punteData] = await Promise.all([
          fetchAllTeams(),
          getMerkbladsByRondteId(rondteId),
          getPunteByBeoordelaar(username)
        ]);
        setTeams([...teamsData].sort((a, b) => a.naam.localeCompare(b.naam)));
        setMerkblads(merkbladsData);
        setMyPunte(punteData);
      } catch (err) {
        setMessage("Fout met laai van spanne: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    loadTeams();
  }, [rondteId, username, skedule]);

  // Check round status (again whenever the schedule changes, e.g. when the round closes)
  useEffect(() => {
    if (!skedule || !skedule.rondte) return;
    const checkRoundStatus = async () => {
      try {
        const status = await getRoundStatus(rondteId);
        setRoundStatus(status);
      } catch (err) {
        setMessage("Fout met laai van rondte status: " + err.message);
      }
    };
    checkRoundStatus();
  }, [rondteId, skedule]);

  // Marking status of a team for this beoordelaar: 'gemerk' (every kriteria on the merkblad
  // has a punt), 'onvoltooi' (only some do, e.g. a kriteria was added later) or 'nie' (none)
  const teamStatus = (spanId) => {
    const scoredMerkblads = new Set(myPunte.filter(p => p.span_id === spanId).map(p => p.merkblad_id));
    const scoredCount = merkblads.filter(m => scoredMerkblads.has(m.merkblad_id)).length;
    if (scoredCount === 0) return 'nie';
    return scoredCount >= merkblads.length ? 'gemerk' : 'onvoltooi';
  };
  // Looking back at an earlier round, only show the teams this beoordelaar marked in it
  const merkbladIds = new Set(merkblads.map(m => m.merkblad_id));
  const teWys = isVorigeRondte
    ? teams.filter(team => myPunte.some(p => p.span_id === team.span_id && merkbladIds.has(p.merkblad_id)))
    : teams;
  // Free choice: every team (for the dropdown)
  const teamsToMark = teWys.filter(team => teamStatus(team.span_id) !== 'gemerk');
  const teamsMarked = teWys.filter(team => teamStatus(team.span_id) === 'gemerk');
  // Schedule: the allocated (not optional) teams, for the progress line
  const toegekendeSpanne = metSkedule
    ? skedule.sessies.filter(s => s.span && !s.opsioneel).map(s => s.span)
    : [];
  const toegekendGemerk = toegekendeSpanne.filter(span => teamStatus(span.span_id) === 'gemerk').length;

  // Handle team selection (also in a closed round, to read the punte)
  const handleTeamClick = async (team) => {
    if (!team) return;

    try {
      setLoading(true);
      setSelectedTeam(team);
      setScores({}); // Reset scores for new team
      setKriteriaDetails({}); // Reset kriteria details
      setIsUpdating(false); // Reset update flag
      setNotaValue("");
      setNotaId(null);

      // Start (or continue) the timer for this team: a new team resets it, the same team keeps
      // the original start so the time continues even after the app has been closed
      const bestaandeTyd = leesSpanTyd();
      const beginTyd = bestaandeTyd && bestaandeTyd.span_id === team.span_id && bestaandeTyd.rondte_id === rondteId
        ? bestaandeTyd.begin
        : stoorSpanTyd(team.span_id, rondteId);
      setSpanBegin(beginTyd);
      setVerstreke(Math.max(0, Math.floor((Date.now() - beginTyd) / 1000)));

      // Everything for the marksheet in one go: the round status (it may have been closed
      // since the page loaded), the merkblad, this beoordelaar's punte and their nota
      const [status, merkbladsData, punteData, notas] = await Promise.all([
        getRoundStatus(rondteId),
        getMerkbladsByRondteId(rondteId),
        getPunteByBeoordelaar(username),
        getNotasBySpanAndBeoordelaar(team.span_id, username).catch(() => [])
      ]);
      setRoundStatus(status);
      setMerkblads(merkbladsData);
      setMyPunte(punteData);
      setKriteriaDetails(kriteriaDetailsFrom(merkbladsData));

      // Fill in the punte this beoordelaar already gave this team in this round
      const merkbladIds = new Set(merkbladsData.map(m => m.merkblad_id));
      const existingScores = punteData.filter(punt => punt.span_id === team.span_id && merkbladIds.has(punt.merkblad_id));
      if (existingScores.length > 0) {
        setIsUpdating(true);
        const existingScoresMap = {};
        existingScores.forEach(punt => {
          // Find the kriteria_id for this punt by matching merkblad_id
          const merkblad = merkbladsData.find(m => m.merkblad_id === punt.merkblad_id);
          if (merkblad) {
            existingScoresMap[merkblad.kriteria_id] = punt.punt.toString();
          }
        });
        setScores(existingScoresMap);
        setMessage(status.is_oop
          ? `Bestaande punte vir ${team.naam} gelaai. U kan dit wysig.`
          : `Die rondte is gesluit: jou punte vir ${team.naam} kan nie meer verander nie.`);
      } else {
        setMessage(status.is_oop ? "" : `Die rondte is gesluit: jy het nie punte vir ${team.naam} gegee nie.`);
      }

      if (notas.length > 0) {
        setNotaId(notas[0].id);
        setNotaValue(notas[0].nota);
      }
    } catch (err) {
      setMessage("Fout met laai van merkblad kriteria: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle score input change with validation
  const handleScoreChange = (kriteriaId, value) => {
    const numValue = parseFloat(value);
    const merkblad = merkblads.find(m => m.kriteria_id === kriteriaId);
    const maxForKriteria = merkblad?.totaal || kriteriaDetails[kriteriaId]?.default_totaal || 100;
    
    // Don't allow values above the maximum for this kriteria
    if (!isNaN(numValue) && numValue > maxForKriteria) {
      return; // Don't update if value exceeds maximum
    }
    
    setScores(prev => ({
      ...prev,
      [kriteriaId]: value
    }));
  };

  // Calculate total score
  const calculateTotalScore = () => {
    return Object.values(scores).reduce((total, score) => {
      const numScore = parseFloat(score);
      return total + (isNaN(numScore) ? 0 : numScore);
    }, 0);
  };

  // Calculate maximum possible score
  const calculateMaxScore = () => {
    return merkblads.reduce((total, merkblad) => {
      const maxForKriteria = merkblad.totaal || kriteriaDetails[merkblad.kriteria_id]?.default_totaal || 100;
      return total + maxForKriteria;
    }, 0);
  };

  // Validate that all kriteria are completed and total is within range
  const validateScores = () => {
    if (merkblads.length === 0) return false;
    
    // Check that all kriteria are completed
    const allCompleted = merkblads.every(merkblad => {
      const score = scores[merkblad.kriteria_id];
      return score !== undefined && score !== null && score !== '';
    });
    
    if (!allCompleted) return false;
    
    // Check that total score is within valid range (0 to max possible)
    const totalScore = calculateTotalScore();
    const maxScore = calculateMaxScore();
    
    return totalScore >= 0 && totalScore <= maxScore;
  };

  // Get validation message for total score
  const getTotalValidationMessage = () => {
    const totalScore = calculateTotalScore();
    const maxScore = calculateMaxScore();
    
    if (totalScore < 0) {
      return "Totale punt kan nie negatief wees nie";
    } else if (totalScore > maxScore) {
      return `Totale punt (${totalScore.toFixed(1)}) kan nie meer as maksimum (${maxScore}) wees nie`;
    }
    return null;
  };

  // Submit scores
  const handleSubmitScores = async () => {
    // Don't allow submission if round is closed
    if (roundStatus && !roundStatus.is_oop) {
      setMessage("Hierdie rondte is gesluit. Jy kan nie punte stuur nie.");
      return;
    }

    if (!validateScores()) {
      const totalValidationMsg = getTotalValidationMessage();
      if (totalValidationMsg) {
        setMessage(totalValidationMsg);
      } else {
        setMessage("Alle kriteria moet voltooi word voordat punte gestuur kan word.");
      }
      return;
    }

    try {
      setSubmitting(true);
      
      // ---- 1. Handle NOTA ----
      if (selectedTeam && auth?.user?.username && notaValue.trim() !== "") {
        if (notaId) {
          // update
          await updateNota(notaId, { nota: notaValue });
        } else {
          // create
          const result = await createNota({ span_id: selectedTeam.span_id, nota: notaValue });
          setNotaId(result.id);
        }
      }
      // ---- END NOTA ----

      // Send a score for every kriteria on the merkblad. The backend creates missing
      // punte and updates existing ones, so kriteria added after a team was first
      // scored are saved too, and resubmitting never creates duplicates.
      const scoreData = {
        span_id: selectedTeam.span_id,
        rondte_id: rondteId,
        kriteria_scores: {}
      };

      merkblads.forEach(merkblad => {
        scoreData.kriteria_scores[merkblad.kriteria_id] = {
          merkblad_id: merkblad.merkblad_id,
          score: parseFloat(scores[merkblad.kriteria_id])
        };
      });

      const bulkResult = await createBulkPunte(scoreData);
      // 207 = some kriteria failed; surface it so the beoordelaar can resubmit
      if (bulkResult.errors && bulkResult.errors.length > 0) {
        throw new Error(bulkResult.errors.join('; '));
      }
      await notifyBulkPunte(scoreData);
      setMessage(isUpdating
        ? `Punte suksesvol gewysig vir ${selectedTeam.naam}!`
        : `Punte suksesvol gestuur vir ${selectedTeam.naam}!`);

      // Keep scores visible after successful submission; the button now shows "Wysig Punte"
      setIsUpdating(true);

      // Reload this beoordelaar's punte so the team moves to "Klaar gemerk" in the team list
      try {
        setMyPunte(await getPunteByBeoordelaar(username));
      } catch (err) {
        console.error("Kon nie punte herlaai nie:", err);
      }
    } catch (err) {
      setMessage("Fout met stuur van punte of nota: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="merke-page">
      {/* Custom slider styles */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          height: 30px;
          width: 30px;
          border-radius: 50%;
          background: #0e1e3b;
          cursor: pointer;
          border: 3px solid #fff;
          box-shadow: 0 2px 6px rgba(14,30,59,0.35);
        }
        
        input[type="range"]::-moz-range-thumb {
          height: 30px;
          width: 30px;
          border-radius: 50%;
          background: #0e1e3b;
          cursor: pointer;
          border: 3px solid #fff;
          box-shadow: 0 2px 6px rgba(14,30,59,0.35);
        }
        
        input[type="range"]::-webkit-slider-track {
          height: 12px;
          background: #dde1e8;
          border-radius: 6px;
        }
        
        input[type="range"]::-moz-range-track {
          height: 12px;
          background: #dde1e8;
          border-radius: 6px;
          border: none;
        }
        
        input[type="range"]:focus {
          outline: none;
        }
        
        input[type="range"]:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px rgba(14, 30, 59, 0.2);
        }
        
        input[type="range"]:focus::-moz-range-thumb {
          box-shadow: 0 0 0 3px rgba(14, 30, 59, 0.2);
        }
      `}</style>

      {/* Only shown when the round is closed: beoordelaars can read their punte but not change them */}
      {roundClosed && (
        <div style={{
          padding: '15px',
          marginBottom: '20px',
          backgroundColor: '#77502d',
          borderRadius: '6px',
          color: '#ffffff'
        }}>
          <h3 style={{ margin: '0 0 5px 0', color: '#ffffff' }}>{skedule?.rondte?.naam || 'Rondte'} is gesluit</h3>
          <p style={{ margin: 0 }}>
            Jy kan jou punte nog sien, maar nie meer verander nie. Hierdie bladsy wys die volgende rondte sodra dit begin.
          </p>
        </div>
      )}

      {/* Look back at an earlier round: only shown when more than one round has started */}
      {rondtesLys.length > 1 && (
        <div style={{ margin: '10px 0 0 0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <label htmlFor="rondte-keuse" style={{ fontWeight: 'bold', color: '#0e1e3b' }}>
            Rondte:
          </label>
          <select
            id="rondte-keuse"
            value={gekoseRondteId ?? ''}
            onChange={(e) => setGekoseRondteId(e.target.value === '' ? null : Number(e.target.value))}
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #0e1e3b', color: '#0e1e3b' }}
          >
            <option value="">
              {rondtesLys.find(r => r.is_huidige)?.naam || 'Huidige rondte'} (huidige)
            </option>
            {rondtesLys.filter(r => !r.is_huidige).map(r => (
              <option key={r.rondte_id} value={r.rondte_id}>{r.naam}</option>
            ))}
          </select>
        </div>
      )}

      {/* Add toggle to show/hide gids field */}
      <div style={{ margin: '10px 0 20px 0', display: 'flex', alignItems: 'center' }}>
        <input
          id="show-gids-toggle"
          type="checkbox"
          checked={showGids}
          onChange={e => setShowGids(e.target.checked)}
          style={{ marginRight: '8px' }}
        />
        <label htmlFor="show-gids-toggle" style={{ fontWeight: 'bold', color: '#0e1e3b' }}>
          Wys gids vir elke kriteria
        </label>
      </div>

      {skedule === undefined ? (
        <div style={{ marginBottom: '30px' }}>Laai skedule...</div>
      ) : !skedule.rondte ? (
        <div style={{
          padding: '15px',
          marginBottom: '30px',
          backgroundColor: '#f0f0f7',
          border: '1px solid #0e1e3b',
          borderRadius: '6px',
          color: '#0e1e3b'
        }}>
          Die rondtes het nog nie begin nie. Hierdie bladsy wys jou spanne sodra die eerste rondte begin.
        </div>
      ) : metSkedule ? (
        // The beoordelaar's schedule: one card per session with the time and their team
        <div style={{ marginBottom: '30px' }}>
          <h2 className="merke-heading">{skedule.rondte.naam}</h2>
          {toegekendeSpanne.length > 0 && (
            <p className="merke-progress">
              Jy het {toegekendGemerk} van {toegekendeSpanne.length} spanne gemerk
            </p>
          )}
          <div className="merke-sessions">
            {skedule.sessies.map(sessie => {
              const span = sessie.span;
              const status = span ? teamStatus(span.span_id) : null;
              const gekies = !!(span && selectedTeam && selectedTeam.span_id === span.span_id);
              const tyd = sessie.begin_tyd || sessie.eind_tyd ? `${sessie.begin_tyd || '?'}–${sessie.eind_tyd || '?'}` : '';
              return (
                <button
                  key={sessie.nommer}
                  type="button"
                  disabled={!span}
                  onClick={() => handleTeamClick(span)}
                  className={"merke-session" + (gekies ? " geselekteer" : "")}
                >
                  <div>
                    <div className="merke-session-times">
                      Sessie {sessie.nommer}{tyd ? ` · ${tyd}` : ''}
                    </div>
                    <div className={"merke-session-team" + (span ? '' : ' leeg')}>
                      {span ? span.naam : 'Geen span hierdie sessie'}
                    </div>
                    {sessie.opsioneel && (
                      <div className="merke-session-noot">Opsioneel: 'n span wat jy nog nie gesien het nie</div>
                    )}
                  </div>
                  {span && (
                    <span
                      className="chip"
                      style={{
                        backgroundColor: status === 'gemerk' ? 'var(--success-bg)' : 'var(--warning-bg)',
                        color: status === 'gemerk' ? 'var(--success)' : 'var(--warning)'
                      }}
                    >
                      {status === 'gemerk' ? '✓ Gemerk' : status === 'onvoltooi' ? 'Onvoltooi' : 'Merk'}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
      // No allocations in this round: pick any team. A native dropdown works well on a phone
      // (it opens the phone's own picker); teams still to mark are listed first.
      <div style={{ marginBottom: '30px' }}>
        <label htmlFor="span-keuse" className="merke-kies-label">
          Kies 'n span
        </label>
        <select
          id="span-keuse"
          className="merke-kies"
          value={selectedTeam ? String(selectedTeam.span_id) : ''}
          onChange={(e) => handleTeamClick(teWys.find(team => String(team.span_id) === e.target.value))}
          disabled={teWys.length === 0}
        >
          <option value="" disabled>
            {teWys.length === 0 ? 'Laai spanne...' : "Kies 'n span..."}
          </option>
          {teamsToMark.length > 0 && (
            <optgroup label={`Nog te merk (${teamsToMark.length})`}>
              {teamsToMark.map(team => (
                <option key={team.span_id} value={team.span_id}>
                  {team.naam}{teamStatus(team.span_id) === 'onvoltooi' ? ' (onvoltooi)' : ''}
                </option>
              ))}
            </optgroup>
          )}
          {teamsMarked.length > 0 && (
            <optgroup label={`Klaar gemerk (${teamsMarked.length})`}>
              {teamsMarked.map(team => (
                <option key={team.span_id} value={team.span_id}>
                  ✓ {team.naam}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        {teWys.length > 0 && (
          <p className="merke-progress" style={{ margin: '8px 0 0 0' }}>
            Jy het {teamsMarked.length} van {teWys.length} spanne gemerk
          </p>
        )}
      </div>
      )}
      {/* Marksheet Form */}
      {selectedTeam && (
        <div className="merke-marksheet">
          <div className="merke-marksheet-title">
            <h2>Merkblad vir {selectedTeam.naam}</h2>
            {spanBegin && (
              <span
                className="chip"
                title="Tyd wat jy al by hierdie span is"
                style={{ backgroundColor: 'var(--surface-3)', color: 'var(--blou-800)' }}
              >
                ⏱ {formatTyd(verstreke)}
              </span>
            )}
            {!loading && (() => {
              const status = teamStatus(selectedTeam.span_id);
              const marked = status === 'gemerk';
              return (
                <span
                  className="chip"
                  style={{
                    backgroundColor: marked ? 'var(--success-bg)' : 'var(--warning-bg)',
                    color: marked ? 'var(--success)' : 'var(--warning)'
                  }}
                >
                  {marked ? '✓ Gemerk' : status === 'onvoltooi' ? 'Onvoltooi' : 'Nog nie gemerk'}
                </span>
              );
            })()}
          </div>
          
          {loading ? (
            <div>Laai kriteria...</div>
          ) : merkblads.length === 0 ? (
            <div style={{ color: '#666' }}>Geen kriteria beskikbaar vir hierdie rondte nie.</div>
          ) : (
            <div>
              <div style={{ marginBottom: '20px' }}>
                {merkblads.map(merkblad => (
                  <div key={merkblad.merkblad_id} className="merke-item">
                    <label className="merke-item-label">
                      {kriteriaDetails[merkblad.kriteria_id]?.beskrywing || `Kriteria ID ${merkblad.kriteria_id}`} ({merkblad.totaal || kriteriaDetails[merkblad.kriteria_id]?.default_totaal || 100}):
                    </label>
                    {/* Conditionally show gids */}
                    {showGids && kriteriaDetails[merkblad.kriteria_id]?.gids && (
                      <div className="merke-gids">
                        {kriteriaDetails[merkblad.kriteria_id].gids}
                      </div>
                    )}
                    <div className="merke-slider-row">
                      <span className="merke-score">
                        {scores[merkblad.kriteria_id] || '0'}
                      </span>
                      <input
                        type="range"
                        min="0"
                        max={merkblad.totaal || kriteriaDetails[merkblad.kriteria_id]?.default_totaal || 100}
                        step="1"
                        value={scores[merkblad.kriteria_id] || 0}
                        onChange={(e) => handleScoreChange(merkblad.kriteria_id, e.target.value)}
                        disabled={roundClosed}
                        style={{
                          flex: 1,
                          height: '12px',
                          background: 'var(--surface-3)',
                          outline: 'none',
                          borderRadius: '6px',
                          cursor: roundClosed ? 'not-allowed' : 'pointer',
                          WebkitAppearance: 'none',
                          appearance: 'none'
                        }}
                        onTouchStart={(e) => {
                          // Prevent scrolling when touching slider on mobile
                          e.preventDefault();
                        }}
                        onTouchMove={(e) => {
                          // Allow slider movement but prevent page scroll
                          e.stopPropagation();
                        }}
                      />
                      {/* <span style={{ minWidth: '60px', color: '#666', fontSize: '14px' }}>
                        / {merkblad.totaal || kriteriaDetails[merkblad.kriteria_id]?.default_totaal || 100}
                      </span> */}
                    </div>
                  </div>
                ))}
              </div>

              {/* Total Score Display */}
              <div className="merke-total">
                <span>
                  <strong>Totale Punt: {calculateTotalScore().toFixed(1)}</strong>
                  <span className="muted"> van {calculateMaxScore()} maksimum</span>
                </span>

                {/* Validation Message */}
                {getTotalValidationMessage() && (
                  <span style={{ color: 'var(--danger)', fontSize: '14px', fontWeight: 'bold' }}>
                    ⚠️ {getTotalValidationMessage()}
                  </span>
                )}
              </div>

              <div style={{ marginTop: '24px', marginBottom: '10px' }}>
                <label htmlFor="team-nota" className="merke-nota-label">Notas oor hierdie span:</label>
                <textarea
                  id="team-nota"
                  className="textarea"
                  rows={6}
                  value={notaValue}
                  onChange={e => setNotaValue(e.target.value)}
                  disabled={!!(roundStatus && !roundStatus.is_oop) || submitting}
                  placeholder="Voer 'n nota in..."
                  style={{ background: !!(roundStatus && !roundStatus.is_oop) ? 'var(--surface-3)' : 'var(--surface)' }}
                />
              </div>

              {roundClosed ? (
                // Closed round: read only
                <p style={{ marginTop: '20px', color: 'var(--brons-700)', fontWeight: 'bold' }}>
                  Die rondte is gesluit: jou punte kan nie meer verander nie.
                </p>
              ) : (
              <div className="merke-submit">
                <div className="merke-submit-inner">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmitScores}
                    disabled={!validateScores() || submitting || (roundStatus && !roundStatus.is_oop)}
                  >
                    {submitting ? (isUpdating ? 'Wysig...' : 'Stuur...') : (isUpdating ? 'Wysig Punte' : 'Stuur Punte')}
                  </button>

                  {!validateScores() && (
                    <span style={{ color: 'var(--warning)', fontSize: '14px' }}>
                      {getTotalValidationMessage() || "Alle kriteria moet voltooi word"}
                    </span>
                  )}
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div
          className={"msg " + (message.includes('suksesvol') ? 'msg-success' : 'msg-warning')}
          style={{ marginTop: '20px' }}
        >
          {message}
        </div>
      )}
    </div>
  );
}

export default Merk;
